import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const CloseButton = ({ onClose }) => (
  <CloseButtonWrapper
    onClick={onClose}
    onTouchStart={(e) => {
      e.stopPropagation();
      onClose();
    }}
    onTouchEnd={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
  >
    <X size={24} color="white" />
  </CloseButtonWrapper>
);

const FullscreenGallery = ({ isOpen, onRequestClose, activity }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const thumbnailsRef = useRef(null);
  const images = activity ? activity.imagesArray : [];

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        navigateImage('prev');
      } else if (event.key === 'ArrowRight') {
        navigateImage('next');
      } else if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleClose = () => {
    setSelectedImageIndex(0);
    onRequestClose();
  };

  const navigateImage = (direction) => {
    setSelectedImageIndex((prevIndex) => {
      if (direction === 'next') {
        return prevIndex < images.length - 1 ? prevIndex + 1 : prevIndex;
      } else if (direction === 'prev') {
        return prevIndex > 0 ? prevIndex - 1 : prevIndex;
      }
      return prevIndex;
    });
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      navigateImage('next');
    } else if (isRightSwipe) {
      navigateImage('prev');
    }
  };

  if (!activity) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        content: {
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          padding: '0',
          border: 'none',
          background: 'transparent',
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1000,
        },
      }}
    >
      <GalleryContainer>
        <ContentArea>
          <CloseButton onClose={handleClose} />
          <ActivityTitle>{activity.activityName}</ActivityTitle>
          <MainImageContainer
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <AnimatePresence initial={false} custom={selectedImageIndex}>
              <MainImage
                key={selectedImageIndex}
                as={motion.img}
                src={images[selectedImageIndex]}
                alt={`Activity image ${selectedImageIndex + 1}`}
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -300 }}
                transition={{ duration: 0.3 }}
              />
            </AnimatePresence>
          </MainImageContainer>
          <ImageCounter>{`${selectedImageIndex + 1} / ${images.length}`}</ImageCounter>
          <ThumbnailsContainer ref={thumbnailsRef}>
            {images.map((image, index) => (
              <Thumbnail
                key={index}
                src={image}
                alt={`Thumbnail ${index}`}
                onClick={() => setSelectedImageIndex(index)}
                isSelected={index === selectedImageIndex}
              />
            ))}
          </ThumbnailsContainer>
        </ContentArea>
      </GalleryContainer>
    </Modal>
  );
};

const GalleryContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
`;

const ActivityTitle = styled.h2`
  color: white;
  text-align: center;
  margin-bottom: 10px;
`;

const MainImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 90%;
  height: 70vh;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    height: 50vh;
  }
`;

const MainImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  user-select: none;
`;

const ImageCounter = styled.div`
  color: white;
  font-size: 16px;
  margin-top: 10px;
`;

const ThumbnailsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  overflow-x: auto;
  width: 80%;
  scroll-behavior: smooth;
  justify-content: center;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const Thumbnail = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 5px;
  cursor: pointer;
  transition: transform 0.3s ease, border-color 0.3s ease;
  border: 2px solid ${props => props.isSelected ? '#e1306c' : 'transparent'};

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 15px;
  z-index: 1001;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default FullscreenGallery;